/**
 * useExternalScript
 *
 * Loads an external <script> tag and tracks its status.
 * Deduplicates by src — multiple callers with the same URL share one script element.
 * Cleans up the script element when no consumers remain.
 */

import { useState, useEffect } from 'react';

type ScriptStatus = 'loading' | 'ready' | 'error';

const loadCounts = new Map<string, number>();

export function useExternalScript(
  src: string | null,
  options?: {
    type?: 'module' | 'text/javascript';
    onLoad?: () => void;
  },
): ScriptStatus {
  const [status, setStatus] = useState<ScriptStatus>(src ? 'loading' : 'error');

  useEffect(() => {
    if (!src) {
      setStatus('error');
      return;
    }

    const existing = document.querySelector<HTMLScriptElement>(`script[src="${CSS.escape(src)}"]`);
    if (existing) {
      loadCounts.set(src, (loadCounts.get(src) || 0) + 1);
      const readyState = existing.getAttribute('data-status');
      setStatus(readyState === 'error' ? 'error' : readyState === 'ready' ? 'ready' : 'loading');

      const onReady = () => { setStatus('ready'); options?.onLoad?.(); };
      const onError = () => setStatus('error');
      existing.addEventListener('load', onReady);
      existing.addEventListener('error', onError);
      return () => {
        existing.removeEventListener('load', onReady);
        existing.removeEventListener('error', onError);
        const remaining = (loadCounts.get(src) || 1) - 1;
        if (remaining <= 0) {
          loadCounts.delete(src);
          existing.remove();
        } else {
          loadCounts.set(src, remaining);
        }
      };
    }

    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    if (options?.type) script.type = options.type;
    script.setAttribute('data-status', 'loading');
    loadCounts.set(src, 1);

    const onReady = () => {
      script.setAttribute('data-status', 'ready');
      setStatus('ready');
      options?.onLoad?.();
    };
    const onError = () => {
      script.setAttribute('data-status', 'error');
      setStatus('error');
    };

    script.addEventListener('load', onReady);
    script.addEventListener('error', onError);
    document.body.appendChild(script);

    return () => {
      script.removeEventListener('load', onReady);
      script.removeEventListener('error', onError);
      const remaining = (loadCounts.get(src) || 1) - 1;
      if (remaining <= 0) {
        loadCounts.delete(src);
        script.remove();
      } else {
        loadCounts.set(src, remaining);
      }
    };
  }, [src]);

  return status;
}
